import type {
  AiChatNode,
  AiChatState,
  AiChatTurn,
} from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import type { PickType } from "@intelligems/ig-types/utils";
import type { UiMessage } from "@intelligems/ig-types/schemas/ai/ui-message";
import type { UiMessageChunkDto } from "@intelligems/ig-types/schemas/ai/ui-message-chunk-dto";
import type {
  UiStreamAction,
  UiStreamHooks,
} from "@intelligems/ig-types/schemas/ai/ui-stream-actions";

import { createInitialAiChatState } from "../initial-state";
import { getOrCreateTurnAndNode } from "./utils";

export class AiChatStateProcessor {
  static handleInitialize(
    draft: AiChatState,
    payload: PickType<UiStreamAction, "initialize">
  ) {
    Object.assign(draft, createInitialAiChatState());
    draft.sessionId = payload.sessionId;
    draft.workflowId = payload.workflowId;
  }

  static handleReset(draft: AiChatState) {
    Object.assign(draft, createInitialAiChatState());
  }

  static isStatePart(
    part: UiMessageChunkDto
  ): part is
    | PickType<UiMessageChunkDto, "start">
    | PickType<UiMessageChunkDto, "finish">
    | PickType<UiMessageChunkDto, "start-step">
    | PickType<UiMessageChunkDto, "finish-step">
    | PickType<UiMessageChunkDto, "workflow-start">
    | PickType<UiMessageChunkDto, "workflow-finish"> {
    return (
      part.type === "start" ||
      part.type === "finish" ||
      part.type === "start-step" ||
      part.type === "finish-step" ||
      part.type === "workflow-start" ||
      part.type === "workflow-finish"
    );
  }

  static createEmptyTurn(draft: AiChatState, turnId: string): AiChatTurn {
    draft.turns.push({
      turnId: turnId || "ai-analytics-turn",
      nodes: [],
    });
    draft.turnLookups[turnId] = draft.turns.length - 1;
    return draft.turns[draft.turns.length - 1];
  }

  static handleAddMessage(
    draft: AiChatState,
    payload: PickType<UiStreamAction, "addMessage">,
    hooks?: UiStreamHooks
  ) {
    // Reconcile by id BEFORE creating turn/node: when a server echo arrives
    // for an optimistically-dispatched message (same client-supplied id), we
    // update the existing slot in place. Calling getOrCreateTurnAndNode first
    // would create an empty server-side turn that never gets messages.
    //
    // The id match is what decides this, not the completeness of the lookup:
    // an id we have already seen is the same message by definition. Requiring
    // a fully-populated location meant a message first added through a path
    // that could not resolve one could never be reconciled, and duplicated in
    // the transcript when the server echoed it back.
    const existing = draft.messageLookups[payload.message.id];
    if (
      existing?.turnIndex !== undefined &&
      existing.nodeIndex !== undefined &&
      existing.messageIndex !== undefined
    ) {
      const existingNode =
        draft.turns[existing.turnIndex]?.nodes[existing.nodeIndex];
      if (existingNode) {
        existingNode.messages[existing.messageIndex] = payload.message;
        AiChatStateProcessor.clearSatisfiedInputRequirement(
          draft,
          payload.message
        );
        hooks?.onMessage?.(payload.message);
        return;
      }
    }

    const { node } = getOrCreateTurnAndNode(draft, payload);
    const message = AiChatStateProcessor.pushMessageToNode(
      draft,
      node,
      payload
    );
    AiChatStateProcessor.clearSatisfiedInputRequirement(draft, message);
    hooks?.onMessage?.(message);
  }

  /**
   * A parked input requirement is answered by the USER replying to it, so only
   * a user message clears it.
   *
   * Clearing on every message discarded the pending requirement on unrelated
   * events — an assistant bubble or a tool echo landing after `setNextAction`
   * silently removed the chips, leaving a proposal on screen with nothing to
   * approve. That is the intermittent half of "proposal renders with no
   * approval chips".
   */
  static clearSatisfiedInputRequirement(
    draft: AiChatState,
    message: UiMessage
  ) {
    if (message.role === "user") {
      draft.nextAction = undefined;
    }
  }

  static createNode(
    draft: AiChatState,
    turn: AiChatTurn,
    node: Omit<AiChatNode, "timestamp" | "messages">
  ): AiChatNode {
    turn.nodes.push({
      ...node,
      messages: [],
    });

    draft.nodeLookups[node.id] = {
      turnIndex: draft.turnLookups[turn.turnId],
      nodeIndex: turn.nodes.length - 1,
    };
    return turn.nodes[turn.nodes.length - 1];
  }

  static pushMessageToNode(
    draft: AiChatState,
    node: AiChatNode,
    payload:
      | PickType<UiStreamAction, "addMessage">
      | PickType<UiStreamAction, "addMessageToLastNode">
  ) {
    node.messages.push(payload.message);
    // Located from the node the message actually landed in, not from the
    // payload: `addMessageToLastNode` carries no `nodeId`, and reading one off
    // the payload wrote an unresolvable location that no later echo could
    // reconcile against.
    const location = draft.nodeLookups[node.id];
    draft.messageLookups[payload.message.id] = {
      turnIndex: location?.turnIndex,
      nodeIndex: location?.nodeIndex,
      messageIndex: node.messages.length - 1,
    };

    return node.messages[node.messages.length - 1];
  }

  static handleSetError(
    draft: AiChatState,
    payload: PickType<UiStreamAction, "setError">
  ) {
    const { node } = getOrCreateTurnAndNode(draft, payload);

    node.status = "error";
    node.errorMessage = payload.message;
  }

  static handleStartWorkflow(
    draft: AiChatState,
    payload: PickType<UiStreamAction, "startWorkflow">
  ) {
    draft.status = "active";
    draft.sessionId = payload.sessionId;
    draft.workflowId = payload.workflowId;
  }

  static handleStart(
    draft: AiChatState,
    payload: PickType<UiStreamAction, "start">
  ) {
    // const { node } = getOrCreateTurn(draft, payload);
  }

  static handleFinish(
    draft: AiChatState,
    payload: PickType<UiStreamAction, "finish">
  ) {
    // const { node } = getOrCreateTurn(draft, payload);
    //
    // node.status = "finished";
  }

  static handleUpdateMetadata(
    draft: AiChatState,
    payload: PickType<UiStreamAction, "updateMetadata">
  ) {
    draft.metadata = {
      ...draft.metadata,
      ...payload.metadata,
    };
  }

  static handleSetEventType(
    draft: AiChatState,
    payload: PickType<UiStreamAction, "setEventType">
  ) {
    // Store event type in metadata for tracking
    draft.metadata = {
      ...draft.metadata,
      eventType: payload.eventType,
    };
  }

  static handleStatePart(
    draft: AiChatState,
    node: AiChatNode,
    part:
      | PickType<UiMessageChunkDto, "start">
      | PickType<UiMessageChunkDto, "finish">
      | PickType<UiMessageChunkDto, "start-step">
      | PickType<UiMessageChunkDto, "finish-step">
      | PickType<UiMessageChunkDto, "workflow-start">
      | PickType<UiMessageChunkDto, "workflow-finish">
  ) {
    switch (part.type) {
      case "start":
      case "start-step":
        node.status = "started";
        draft.status = "processing";
        break;
      case "workflow-start":
        draft.status = "processing";
        break;
      case "finish":
      case "finish-step":
        if (node.status !== "error") {
          node.status = "finished";
        }
        break;
      case "workflow-finish":
        draft.status = "waiting_for_input";
        if ("variables" in part && part.variables) {
          draft.variables = part.variables;
        }
        break;
    }
  }
}
